import { type XYPosition } from '@xyflow/react';

import { type PhaseBandNode } from '../types';

/**
 * Geometry for diagrams that divide their tiles into ordered phases, such as
 * the death sections of a presentation diagram or the stages of a generated
 * diagram. Phases follow each other from top to bottom: phase 0 is at the top,
 * and each later phase is below it. A boundary is the space between two
 * adjacent phases, where a diagram can draw a band.
 *
 * These functions do not know the node types or the phase model of a diagram.
 * The caller describes them with a {@link PhaseGeometry}.
 */

interface PhaseNode {
  id: string;
  position: XYPosition;
}

/** Tells the phase functions how the nodes of one diagram occupy phases. */
export interface PhaseGeometry<TNode extends PhaseNode> {
  /** The phase index of the node. Undefined for a node in no phase. */
  phaseOf: (node: TNode) => number | undefined;
  /** The height of the node, in canvas pixels. */
  sizeOf: (node: TNode) => number;
}

/** The vertical space that the nodes of one phase occupy. */
export interface PhaseExtent {
  start: number;
  end: number;
}

/** Returns the extent of each phase that has at least one node. */
export function getPhaseExtents<TNode extends PhaseNode>(
  nodes: TNode[],
  { phaseOf, sizeOf }: PhaseGeometry<TNode>
): Map<number, PhaseExtent> {
  const extentByPhase = new Map<number, PhaseExtent>();
  for (const node of nodes) {
    const phase = phaseOf(node);
    if (phase === undefined) continue;

    const start = node.position.y;
    const end = start + sizeOf(node);
    const extent = extentByPhase.get(phase);
    if (!extent) {
      extentByPhase.set(phase, { start, end });
      continue;
    }
    extent.start = Math.min(extent.start, start);
    extent.end = Math.max(extent.end, end);
  }
  return extentByPhase;
}

/**
 * The one phase whose extent changed. Returns null if no phase changed, if two
 * phases changed, or if a phase gained or lost all of its nodes.
 *
 * Two phases that change together are a selection across a boundary. No one
 * phase drives the others then, so no phase moves.
 */
function findChangedPhase(
  previousExtents: Map<number, PhaseExtent>,
  extents: Map<number, PhaseExtent>
): number | null {
  if (previousExtents.size !== extents.size) return null;

  let changedPhase: number | null = null;
  for (const [phase, extent] of Array.from(extents.entries())) {
    const previousExtent = previousExtents.get(phase);
    if (!previousExtent) return null;
    if (
      previousExtent.start === extent.start &&
      previousExtent.end === extent.end
    )
      continue;
    if (changedPhase !== null) return null;
    changedPhase = phase;
  }
  return changedPhase;
}

/** Returns the minimum space between the nodes of two adjacent phases. */
export type PhaseGap = (earlierPhase: number, laterPhase: number) => number;

/**
 * How far each other phase must move to stay clear of the phase that changed.
 *
 * A phase does not move until the changed phase comes within the minimum gap
 * of it. The changed phase then pushes it ahead. A phase never goes further
 * from its baseline than this, so it comes back as the gesture comes back.
 *
 * A gap that only becomes larger does not move a phase. If it did, a phase
 * with one tile would move all of the other phases by the same distance, and
 * the diagram would follow the cursor.
 */
function getCascadeShifts(
  changedPhase: number,
  baselineExtents: Map<number, PhaseExtent>,
  extents: Map<number, PhaseExtent>,
  minimumGap: PhaseGap
): Map<number, number> {
  const shiftByPhase = new Map<number, number>();
  const phases = Array.from(extents.keys()).sort((a, b) => a - b);
  const changedIndex = phases.indexOf(changedPhase);
  const changedExtent = extents.get(changedPhase);
  if (!changedExtent) return shiftByPhase;

  // Go forward from the changed phase, then back from it. Each phase stays at
  // its baseline, or moves off the baseline just enough to keep the gap.
  let earlierEnd = changedExtent.end;
  for (let index = changedIndex + 1; index < phases.length; index += 1) {
    const phase = phases[index]!;
    const baseline = baselineExtents.get(phase);
    const extent = extents.get(phase)!;
    if (!baseline) break;

    const start = Math.max(
      baseline.start,
      earlierEnd + minimumGap(phases[index - 1]!, phase)
    );
    if (start !== extent.start) shiftByPhase.set(phase, start - extent.start);
    earlierEnd = start + (baseline.end - baseline.start);
  }

  let laterStart = changedExtent.start;
  for (let index = changedIndex - 1; index >= 0; index -= 1) {
    const phase = phases[index]!;
    const baseline = baselineExtents.get(phase);
    const extent = extents.get(phase)!;
    if (!baseline) break;

    const end = Math.min(
      baseline.end,
      laterStart - minimumGap(phase, phases[index + 1]!)
    );
    if (end !== extent.end) shiftByPhase.set(phase, end - extent.end);
    laterStart = end - (baseline.end - baseline.start);
  }

  return shiftByPhase;
}

interface CascadePhasesInput<TNode extends PhaseNode> {
  /** The nodes after the change. */
  nodes: TNode[];
  /** The nodes before the change. They tell which phase the user changed. */
  previousNodes: TNode[];
  /**
   * The nodes when the current gesture started. Each phase is measured
   * against these, so a phase pushed aside during a gesture goes back when
   * the gesture goes back.
   */
  baselineNodes: TNode[];
  geometry: PhaseGeometry<TNode>;
  minimumGap: PhaseGap;
}

/**
 * Moves the phases that a change did not touch, so that they stay clear of
 * the one phase that it did touch. A phase moves up or down as a whole.
 *
 * A boundary never stops a node, because the diagram data, not the position,
 * sets the phase of a node. When the user moves a node towards a boundary,
 * the boundary moves with the node, and pushes the phases after it.
 */
export function cascadePhases<TNode extends PhaseNode>({
  nodes,
  previousNodes,
  baselineNodes,
  geometry,
  minimumGap,
}: CascadePhasesInput<TNode>): TNode[] {
  const extents = getPhaseExtents(nodes, geometry);
  const changedPhase = findChangedPhase(
    getPhaseExtents(previousNodes, geometry),
    extents
  );
  if (changedPhase === null) return nodes;

  const shiftByPhase = getCascadeShifts(
    changedPhase,
    getPhaseExtents(baselineNodes, geometry),
    extents,
    minimumGap
  );
  if (shiftByPhase.size === 0) return nodes;

  return nodes.map((node) => {
    const phase = geometry.phaseOf(node);
    const shift = phase === undefined ? undefined : shiftByPhase.get(phase);
    if (!shift) return node;
    return {
      ...node,
      position: { ...node.position, y: node.position.y + shift },
    };
  });
}

interface PhaseBoundaryOptions {
  /** The largest distance from the earlier phase to the boundary. */
  maxOffset?: number;
  /** The smallest distance from the earlier phase to the boundary. */
  minOffset?: number;
}

/**
 * Returns the y position of the boundary after `phase`. The
 * boundary is in the middle of the space between the phases, but not more
 * than `maxOffset` after the earlier phase. A later node that comes near the
 * boundary thus pushes it back, instead of crossing it.
 *
 * Returns null when one side of the boundary has no node. There is no
 * transition to show between a phase and nothing.
 */
export function getPhaseBoundary<TNode extends PhaseNode>(
  nodes: TNode[],
  { phaseOf, sizeOf }: PhaseGeometry<TNode>,
  phase: number,
  {
    maxOffset = Number.POSITIVE_INFINITY,
    minOffset = 0,
  }: PhaseBoundaryOptions = {}
): number | null {
  let earlierEnd = Number.NEGATIVE_INFINITY;
  let laterStart = Number.POSITIVE_INFINITY;
  for (const node of nodes) {
    const nodePhase = phaseOf(node);
    if (nodePhase === undefined) continue;
    if (nodePhase <= phase)
      earlierEnd = Math.max(earlierEnd, node.position.y + sizeOf(node));
    else laterStart = Math.min(laterStart, node.position.y);
  }
  if (!Number.isFinite(earlierEnd) || !Number.isFinite(laterStart)) return null;

  return Math.max(
    earlierEnd + minOffset,
    Math.min(earlierEnd + maxOffset, (earlierEnd + laterStart) / 2)
  );
}

/** A band that a diagram draws in the boundary after `belowPhaseIndex`. */
export interface PhaseBand {
  label: string;
  belowPhaseIndex: number;
  /** Optional. When set, a click on the band label calls it. */
  onEdit?: () => void;
}

interface PhaseBandNodesInput<TNode extends PhaseNode> {
  nodes: TNode[];
  bands: PhaseBand[];
  geometry: PhaseGeometry<TNode>;
  /** The width of the node, in canvas pixels. */
  widthOf: (node: TNode) => number;
  /** The height of each band node box, in canvas pixels. */
  height: number;
  /** Space at the left of the nodes for the band labels, in canvas pixels. */
  gutter?: number;
  /** Where each band goes in its boundary. See {@link getPhaseBoundary}. */
  boundary?: PhaseBoundaryOptions;
}

/**
 * Returns a `phaseBand` node for each band at the current node positions. The
 * node box covers the nodes in a phase and the gutter at their left.
 * PhaseBandNode draws the rule and the label against the visible canvas, so
 * the box only gives the node a position.
 *
 * A band goes at the boundary that {@link getPhaseBoundary} returns. A band
 * whose boundary has no node on one side does not show.
 */
export function getPhaseBandNodes<TNode extends PhaseNode>({
  nodes,
  bands,
  geometry,
  widthOf,
  height,
  gutter = 0,
  boundary,
}: PhaseBandNodesInput<TNode>): PhaseBandNode[] {
  const phasedNodes = nodes.filter(
    (node) => geometry.phaseOf(node) !== undefined
  );
  if (phasedNodes.length === 0) return [];

  const left = Math.min(...phasedNodes.map((node) => node.position.x));
  const right = Math.max(
    ...phasedNodes.map((node) => node.position.x + widthOf(node))
  );
  return bands.flatMap((band, index) => {
    const center = getPhaseBoundary(
      phasedNodes,
      geometry,
      band.belowPhaseIndex,
      boundary
    );
    if (center === null) return [];
    return [
      {
        id: `phase-band:${index}`,
        type: 'phaseBand' as const,
        position: { x: left - gutter, y: center - height / 2 },
        width: right - left + gutter,
        height,
        zIndex: -1,
        selectable: false,
        draggable: false,
        focusable: false,
        data: { label: band.label, onEdit: band.onEdit },
      },
    ];
  });
}
